/**
 * `Clear Format` module is used to handle Clear Format.
 */
import { closest, isNullOrUndefined as isNOU } from '@syncfusion/ej2-base';
import { NodeSelection } from './../../selection/index';
import { NodeCutter } from './nodecutter';
import { DOMNode } from './dom-node';
import { InsertMethods } from './insert-methods';
import { IsFormatted } from './isformatted';
import { isIDevice, setEditFrameFocus } from '../../common/util';
import { TableSelection } from './table-selection';
export class ClearFormat {
    /**
     * clear method
     *
     * @param {Document} docElement - specifies the document element.
     * @param {Node} endNode - specifies the end node
     * @param {string} enterAction - specifies the enter key action
     * @param {string} selector - specifies the string value
     * @param {string} command - specifies the command value
     * @returns {void}
     * @hidden
     * @private
     */
    static clear(docElement, endNode, enterAction, selector, command) {
        this.domNode = new DOMNode(endNode, docElement);
        this.defaultTag = enterAction === 'P' ? 'p' : 'div';
        const nodeSelection = new NodeSelection(endNode);
        const nodeCutter = new NodeCutter();
        let range = nodeSelection.getRange(docElement);
        const nodes = range.collapsed ? nodeSelection.getSelectionNodeCollection(range) :
            nodeSelection.getSelectionNodeCollectionBr(range);
        const save = nodeSelection.save(range, docElement);
        let cursorRange = false;
        if (range.collapsed && command !== 'ClearFormat') {
            cursorRange = true;
            range = nodeCutter.GetCursorRange(docElement, range, nodes[0]);
        }
        const isCollapsed = range.collapsed;
        let selectedCells;
        if (isCollapsed && command === 'ClearFormat') {
            const start = range.startContainer.nodeType === 3 ?
                range.startContainer.parentNode : range.startContainer;
            const curTable = start.closest('table');
            selectedCells = !isNOU(curTable) ? Array.from(curTable.querySelectorAll('.e-cell-select')) : null;
        }
        if (!isCollapsed) {
            let preNode;
            if (nodes.length > 0 && nodes[0].nodeName === 'BR' && closest(nodes[0], 'table')) {
                preNode = nodeCutter.GetSpliceNode(range, closest(nodes[0], 'table'));
            }
            else {
                preNode = nodeCutter.GetSpliceNode(range, nodes[nodes.length > 1 && nodes[0].nodeName === 'IMG' ? 1 : 0]);
            }
            if (nodes.length === 1) {
                nodeSelection.setSelectionContents(docElement, preNode);
                range = nodeSelection.getRange(docElement);
            }
            else if (nodes.length > 1) {
                let i = 1;
                let lastText = nodes[nodes.length - i];
                while (nodes.length <= i && nodes[nodes.length - i].nodeName === 'BR') {
                    i++;
                    lastText = nodes[nodes.length - i];
                }
                const lasNode = nodeCutter.GetSpliceNode(range, lastText);
                if (!isNOU(lasNode) && !isNOU(preNode)) {
                    nodeSelection.setSelectionText(docElement, preNode, lasNode, 0, (lasNode.nodeType === 3) ?
                        lasNode.textContent.length : lasNode.childNodes.length);
                }
                range = nodeSelection.getRange(docElement);
            }
            let exactNodes = nodeSelection.getNodeCollection(range);
            const cloneSelectNodes = exactNodes.slice();
            this.clearInlines(nodeSelection.getSelectionNodesBr(cloneSelectNodes), cloneSelectNodes, nodeSelection.getRange(docElement), nodeCutter, endNode);
            this.reSelection(docElement, save, exactNodes);
            range = nodeSelection.getRange(docElement);
            exactNodes = nodeSelection.getNodeCollection(range);
            const cloneParentNodes = exactNodes.slice();
            this.clearBlocks(docElement, cloneParentNodes, endNode, nodeCutter, nodeSelection);
            if (isIDevice()) {
                setEditFrameFocus(endNode, selector);
            }
            this.reSelection(docElement, save, exactNodes);
        }
        else if (!isNOU(selectedCells) && selectedCells.length > 0) {
            const tableSelection = new TableSelection(docElement.body, docElement);
            const textNodes = tableSelection.getTextNodes();
            this.clearInlines(textNodes, textNodes, nodeSelection.getRange(docElement), nodeCutter, endNode, true);
            this.unWrap(docElement, selectedCells, nodeCutter, nodeSelection);
        }
        if (cursorRange) {
            nodeSelection.setCursorPoint(docElement, range.endContainer, range.endOffset);
        }
        docElement.querySelectorAll('.e-rte-table-processed').forEach((table) => {
            table.classList.remove('e-rte-table-processed');
        });
    }
    static reSelection(docElement, save, exactNodes) {
        const selectionNodes = save.getInsertNodes(exactNodes);
        save.startContainer = save.getNodeArray(selectionNodes[0], true, docElement);
        save.startOffset = 0;
        save.endContainer = save.getNodeArray(selectionNodes[selectionNodes.length - 1], false, docElement);
        const endIndexNode = selectionNodes[selectionNodes.length - 1];
        save.endOffset = (endIndexNode.nodeType === 3) ? endIndexNode.textContent.length
            : endIndexNode.childNodes.length;
        save.restore();
    }
    static clearBlocks(docElement, nodes, endNode, nodeCutter, nodeSelection) {
        let parentNodes = [];
        for (let index = 0; index < nodes.length; index++) {
            if (this.BLOCK_TAGS.indexOf(nodes[index].nodeName.toLocaleLowerCase()) > -1
                && parentNodes.indexOf(nodes[index]) === -1) {
                parentNodes.push(nodes[index]);
            }
            else if ((this.BLOCK_TAGS.indexOf(nodes[index].parentNode.nodeName.toLocaleLowerCase()) > -1)
                && parentNodes.indexOf(nodes[index].parentNode) === -1
                && endNode !== nodes[index].parentNode) {
                parentNodes.push(nodes[index].parentNode);
            }
        }
        parentNodes = this.spliceParent(parentNodes, nodes)[0];
        parentNodes = this.removeParent(parentNodes);
        this.unWrap(docElement, parentNodes, nodeCutter, nodeSelection);
    }
    static spliceParent(parentNodes, nodes) {
        for (let index1 = 0; index1 < parentNodes.length; index1++) {
            const len = parentNodes[index1].childNodes.length;
            for (let index2 = 0; index2 < len; index2++) {
                if ((nodes.indexOf(parentNodes[index1].childNodes[index2]) > 0)
                    && (parentNodes[index1].childNodes[index2].childNodes.length > 0)) {
                    nodes = this.spliceParent([parentNodes[index1].childNodes[index2]], nodes)[1];
                }
                if ((nodes.indexOf(parentNodes[index1].childNodes[index2]) <= -1) &&
                    (parentNodes[index1].childNodes[index2].textContent.trim() !== '')) {
                    for (let index3 = 0; index3 < len; index3++) {
                        if (nodes.indexOf(parentNodes[index1].childNodes[index3]) > -1) {
                            nodes.splice(nodes.indexOf(parentNodes[index1].childNodes[index3]), 1);
                        }
                    }
                    index2 = parentNodes[index1].childNodes.length;
                    const parentIndex = parentNodes.indexOf(parentNodes[index1].parentNode);
                    const nodeIndex = nodes.indexOf(parentNodes[index1].parentNode);
                    if (parentIndex > -1) {
                        parentNodes.splice(parentIndex, 1);
                    }
                    if (nodeIndex > -1) {
                        nodes.splice(nodeIndex, 1);
                    }
                    const elementIndex = nodes.indexOf(parentNodes[index1]);
                    if (elementIndex > -1) {
                        nodes.splice(elementIndex, 1);
                    }
                    parentNodes.splice(index1, 1);
                    index1--;
                }
            }
        }
        return [parentNodes, nodes];
    }
    static removeChild(parentNodes, parentNode) {
        const count = parentNode.childNodes.length;
        if (count > 0) {
            for (let index = 0; index < count; index++) {
                if (parentNodes.indexOf(parentNode.childNodes[index]) > -1) {
                    parentNodes = this.removeChild(parentNodes, parentNode.childNodes[index]);
                    parentNodes.splice(parentNodes.indexOf(parentNode.childNodes[index]), 1);
                }
            }
        }
        return parentNodes;
    }
    static removeParent(parentNodes) {
        for (let index = 0; index < parentNodes.length; index++) {
            if (parentNodes.indexOf(parentNodes[index].parentNode) > -1) {
                parentNodes = this.removeChild(parentNodes, parentNodes[index]);
                parentNodes.splice(index, 1);
                index--;
            }
        }
        return parentNodes;
    }
    static unWrap(docElement, parentNodes, nodeCutter, nodeSelection) {
        for (let index1 = 0; index1 < parentNodes.length; index1++) {
            parentNodes[index1] = (closest(parentNodes[index1], 'li') && parentNodes[index1].nodeName !== 'UL' && parentNodes[index1].nodeName !== 'OL') &&
                isNOU(closest(parentNodes[index1], 'li').querySelector('.e-richtexteditor')) ? closest(parentNodes[index1], 'li')
                : parentNodes[index1];
            if (this.NONVALID_TAGS.indexOf(parentNodes[index1].nodeName.toLowerCase()) > -1
                && parentNodes[index1].parentNode
                && this.NONVALID_PARENT_TAGS.indexOf(parentNodes[index1].parentNode.nodeName.toLowerCase()) > -1) {
                nodeSelection.setSelectionText(docElement, parentNodes[index1], parentNodes[index1], 0, parentNodes[index1].childNodes.length);
                InsertMethods.unwrap(nodeCutter.GetSpliceNode(nodeSelection.getRange(docElement), parentNodes[index1].parentNode));
            }
            const blockquoteNode = closest(parentNodes[index1], 'blockquote');
            if (parentNodes[index1].nodeName.toLocaleLowerCase() !== 'blockquote' && !isNOU(blockquoteNode) && blockquoteNode.textContent === parentNodes[index1].textContent) {
                const blockNodes = this.removeParent([blockquoteNode]);
                this.unWrap(docElement, blockNodes, nodeCutter, nodeSelection);
            }
            if (parentNodes[index1].nodeName.toLocaleLowerCase() !== 'p') {
                if (this.NONVALID_PARENT_TAGS.indexOf(parentNodes[index1].nodeName.toLowerCase()) < 0
                    && !((parentNodes[index1].nodeName.toLocaleLowerCase() === 'blockquote'
                        || parentNodes[index1].nodeName.toLocaleLowerCase() === 'li')
                        && this.IGNORE_PARENT_TAGS.indexOf(parentNodes[index1].childNodes[0].nodeName.toLocaleLowerCase()) > -1)
                    && !(parentNodes[index1].childNodes.length === 1
                        && parentNodes[index1].childNodes[0].nodeName.toLocaleLowerCase() === 'p')) {
                    const target = parentNodes[index1];
                    if (['TABLE', 'TD', 'TH', 'TBODY', 'TR'].indexOf(target.nodeName) !== -1) {
                        if (target.hasAttribute('style')) {
                            target.removeAttribute('style'); // Remove style if present
                        }
                        if (target.hasAttribute('class')) {
                            const allowedClasses = new Set(['e-rte-table', 'e-rte-paste-table', 'e-rte-custom-table', 'e-cell-select', 'e-multi-cells-select']);
                            const filteredClasses = target.className
                                .split(/\s+/)
                                .filter((cls) => allowedClasses.has(cls));
                            if (filteredClasses.length > 0) {
                                target.className = filteredClasses.join(' ');
                            }
                            else {
                                target.removeAttribute('class');
                            }
                        }
                        const currentTable = target.closest('table');
                        if (target.nodeName === 'TH' && target.closest('tr') && target.closest('thead') && target.closest('table')) {
                            this.convertHeaderToBodyRow(target, docElement);
                        }
                        if (currentTable && !currentTable.classList.contains('e-rte-table-processed')) {
                            this.addEligibleParentsUpToTable(target, parentNodes);
                        }
                    }
                    else {
                        InsertMethods.Wrap(parentNodes[index1], docElement.createElement(this.defaultTag));
                    }
                }
                let childNodes = [];
                if (parentNodes[index1].nodeName === 'TABLE' || parentNodes[index1].nodeName === 'TD' || parentNodes[index1].nodeName === 'TH' ||
                    parentNodes[index1].nodeName === 'TBODY' || parentNodes[index1].nodeName === 'TR') {
                    childNodes = Array.from(parentNodes[index1].childNodes);
                }
                else {
                    childNodes = InsertMethods.unwrap(parentNodes[index1]);
                }
                if (childNodes.length === 1
                    && childNodes[0].parentNode.nodeName.toLocaleLowerCase() === 'p') {
                    InsertMethods.Wrap(parentNodes[index1], docElement.createElement(this.defaultTag));
                    InsertMethods.unwrap(parentNodes[index1]);
                }
                for (let index2 = 0; index2 < childNodes.length; index2++) {
                    if (this.NONVALID_TAGS.indexOf(childNodes[index2].nodeName.toLowerCase()) > -1) {
                        this.unWrap(docElement, [childNodes[index2]], nodeCutter, nodeSelection);
                    }
                    else if (this.BLOCK_TAGS.indexOf(childNodes[index2].nodeName.toLocaleLowerCase()) > -1 &&
                        childNodes[index2].nodeName.toLocaleLowerCase() !== 'p') {
                        const blockNodes = this.removeParent([childNodes[index2]]);
                        this.unWrap(docElement, blockNodes, nodeCutter, nodeSelection);
                    }
                    else if (this.BLOCK_TAGS.indexOf(childNodes[index2].nodeName.toLocaleLowerCase()) > -1 &&
                        childNodes[index2].nodeName.toLocaleLowerCase() === 'p') {
                        if (childNodes[index2].parentNode.nodeName.toLocaleLowerCase() === 'p') {
                            InsertMethods.unwrap(childNodes[index2].parentNode);
                        }
                        InsertMethods.Wrap(childNodes[index2], docElement.createElement(this.defaultTag));
                        InsertMethods.unwrap(childNodes[index2]);
                    }
                    else if (this.BLOCK_TAGS.indexOf(childNodes[index2].nodeName.toLocaleLowerCase()) > -1 &&
                        childNodes[index2].parentNode.nodeName.toLocaleLowerCase() ===
                            childNodes[index2].nodeName.toLocaleLowerCase()) {
                        InsertMethods.unwrap(childNodes[index2]);
                    }
                }
            }
            else {
                InsertMethods.Wrap(parentNodes[index1], docElement.createElement(this.defaultTag));
                InsertMethods.unwrap(parentNodes[index1]);
            }
        }
    }
    static addEligibleParentsUpToTable(target, parentNodes) {
        const includedNodes = new Set(parentNodes);
        let currentNode = target;
        while (currentNode && currentNode.parentNode) {
            if (currentNode.nodeName === 'TABLE') {
                currentNode.classList.add('e-rte-table-processed');
                break;
            }
            const parentNodeRef = currentNode.parentNode;
            const childElements = Array.from(parentNodeRef.children);
            const hasSingleChild = childElements.length <= 1;
            const areAllChildrenIncluded = childElements.length > 0 && childElements.every((child) => includedNodes.has(child));
            if (!(hasSingleChild || areAllChildrenIncluded)) {
                break;
            }
            if (!includedNodes.has(parentNodeRef)) {
                parentNodes.push(parentNodeRef);
                includedNodes.add(parentNodeRef);
            }
            currentNode = parentNodeRef;
        }
    }
    static convertHeaderToBodyRow(target, docElement) {
        const table = target.closest('table');
        if (!table) {
            return;
        }
        const tbody = table.querySelector('tbody');
        const rowsToMove = [];
        if (target.nodeName === 'TH') {
            const tableRow = target.closest('tr');
            if (tableRow && tableRow.parentElement.nodeName === 'THEAD') {
                rowsToMove.push(tableRow);
            }
        }
        else if (target.nodeName === 'TR') {
            rowsToMove.push(target);
        }
        rowsToMove.forEach((tr) => {
            const cells = Array.from(tr.childNodes);
            cells.forEach((cell) => {
                if (cell.nodeName === 'TH') {
                    const td = docElement.createElement('td');
                    while (cell.firstChild) {
                        td.appendChild(cell.firstChild);
                    }
                    Array.from(cell.attributes).forEach((attr) => {
                        td.setAttribute(attr.name, attr.value);
                    });
                    tr.replaceChild(td, cell);
                }
            });
            const thead = tr.parentElement;
            if (tbody.firstChild) {
                tbody.insertBefore(tr, tbody.firstChild);
            }
            else {
                tbody.appendChild(tr);
            }
            if (thead && thead.nodeName === 'THEAD' && thead.innerText.trim() === '') {
                thead.remove();
            }
        });
    }
    static clearInlines(textNodes, nodes, range, nodeCutter, 
    // eslint-disable-next-line
    endNode, isTableSelected) {
        for (let index = 0; index < textNodes.length; index++) {
            let currentInlineNode = textNodes[index];
            let currentNode;
            while (!this.domNode.isBlockNode(currentInlineNode) &&
                (currentInlineNode.parentElement && (!(currentInlineNode.parentElement.classList.contains('e-img-inner') ||
                    currentInlineNode.parentElement.classList.contains('e-img-caption-text'))))) {
                currentNode = currentInlineNode;
                currentInlineNode = currentInlineNode.parentElement;
            }
            if (currentNode &&
                IsFormatted.inlineTags.indexOf(currentNode.nodeName.toLocaleLowerCase()) > -1 &&
                currentNode.nodeName.toLocaleLowerCase() !== 'a') {
                if (!isTableSelected) {
                    nodeCutter.GetSpliceNode(range, currentNode);
                }
                this.removeInlineParent(currentNode);
            }
        }
    }
    static removeInlineParent(textNodes) {
        const nodes = InsertMethods.unwrap(textNodes);
        for (let index = 0; index < nodes.length; index++) {
            const parent = nodes[index].parentNode;
            const nodeName = nodes[index].nodeName.toLocaleLowerCase();
            const parentName = parent && parent.nodeName ? parent.nodeName.toLocaleLowerCase() : '';
            // do not unwrap anchor elements
            if (parent && parent.childNodes.length === 1 && !(parent.classList.contains('e-img-inner') ||
                parent.classList.contains('e-img-caption-text'))
                && parentName !== 'a'
                && IsFormatted.inlineTags.indexOf(parentName) > -1) {
                this.removeInlineParent(parent);
            }
            else if (nodeName !== 'a' && IsFormatted.inlineTags.indexOf(nodeName) > -1) {
                this.removeInlineParent(nodes[index]);
            }
        }
    }
}
ClearFormat.BLOCK_TAGS = ['address', 'article', 'aside', 'blockquote',
    'details', 'dd', 'div', 'dl', 'dt', 'fieldset', 'figcaption', 'figure', 'footer',
    'form', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'header', 'hgroup', 'li', 'main', 'nav',
    'noscript', 'ol', 'p', 'pre', 'section', 'ul', 'table', 'td', 'th', 'tbody', 'tr'];
ClearFormat.NONVALID_PARENT_TAGS = ['thead', 'ul', 'ol', 'tfoot'];
ClearFormat.IGNORE_PARENT_TAGS = ['ul', 'ol', 'table'];
ClearFormat.NONVALID_TAGS = ['thead', 'figcaption', 'tr', 'tfoot', 'figcaption', 'li'];
ClearFormat.defaultTag = 'p';
